import { Component, type ErrorInfo, type ReactNode } from 'react';
import { t } from '../i18n';

interface Props {
  children: ReactNode;
}
interface State {
  error: Error | null;
}

/**
 * App-level error boundary: catches render/runtime errors in the React tree and
 * shows a friendly, brand-styled recovery screen instead of a white page. Your
 * documents are safe in local storage — reloading recovers the app.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('markbird crashed:', error, info.componentStack);
  }

  override render(): ReactNode {
    if (!this.state.error) return this.props.children;
    return (
      <div className="mb-crash" role="alert">
        <div className="mb-crash-card">
          <h1>{t('crash.title')}</h1>
          <p>{t('crash.body')}</p>
          <pre className="mb-crash-detail">{this.state.error.message}</pre>
          <div className="mb-crash-actions">
            <button className="mb-btn mb-btn-primary" onClick={() => window.location.reload()}>
              {t('crash.reload')}
            </button>
            <button className="mb-btn" onClick={() => this.setState({ error: null })}>
              {t('crash.continue')}
            </button>
          </div>
        </div>
      </div>
    );
  }
}
